import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  CURSOR_QUERY_PARAM,
  DataTable,
  LIMIT_QUERY_PARAM,
} from '@/components/ui/data-table';
import { TableTitle } from '@/components/ui/table-title';
import { ImportSwaggerDialog } from '@/features/internal-admin/components/import-swagger-dialog';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { useAdminSwaggersTable } from '@/features/internal-admin/lib/admin-swaggers-table';
import {
  SeekPage,
  SwaggerDefinitionStatus,
  SwaggerDefinitionSummary,
} from '@activepieces/shared';

const DEFAULT_PAGE_SIZE = 10;

const AdminSwaggersPage = () => {
  const { columns, filters } = useAdminSwaggersTable();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryParams = {
    cursor: searchParams.get(CURSOR_QUERY_PARAM) || undefined,
    limit: Number(searchParams.get(LIMIT_QUERY_PARAM)) || DEFAULT_PAGE_SIZE,
    search: searchParams.get('displayName') ?? undefined,
    tenantId: searchParams.get('tenantIds') ?? undefined,
    status:
      searchParams.getAll('status').length > 0
        ? (searchParams.getAll('status') as SwaggerDefinitionStatus[])
        : undefined,
  };

  const { data, isLoading, isError } = useQuery<
    SeekPage<SwaggerDefinitionSummary>,
    Error
  >({
    queryKey: ['admin-swaggers', queryParams],
    queryFn: () => adminApi.listSwaggers(queryParams),
  });

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('Failed to load Swaggers')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center justify-between">
        <TableTitle
          description={t(
            'Import an API description and expose it to one or more clients',
          )}
        >
          {t('Swaggers')}
        </TableTitle>
        <ImportSwaggerDialog />
      </div>
      <DataTable
        columns={columns}
        page={data}
        isLoading={isLoading}
        filters={filters}
        onRowClick={(row, newWindow) => {
          const url = `/internal-admin/swaggers/${row.id}`;
          if (newWindow) {
            window.open(url, '_blank');
          } else {
            navigate(url);
          }
        }}
      />
    </div>
  );
};

export default AdminSwaggersPage;
